/**
 * CORE-1059 — redact bearer secrets that ride in URL query strings before an
 * event is handed to Sentry.
 *
 * SignalR cannot send the JWT as an `Authorization` header during the WebSocket
 * upgrade, so it appends it to the query: `/hubs/watch-party?...&access_token=<jwt>`
 * (see useWatchPartyHub / useLiveChat / useDmChat / useTranslation …). When any
 * of those connections errors, `@sentry/react-native` captures the request URL
 * on the event (`event.request.url`), on the fetch/xhr breadcrumb
 * (`breadcrumb.data.url`) and often inside the thrown message — so a live JWT
 * would leave the browser and land in the Sentry issue, exactly the leak the
 * nginx side of this ticket closes on the edge. CORE-1038 did the same on the
 * .NET side (SecretScrubber): there the secret sat in the *path*; here it sits
 * in the *query*, so we scrub the query value while leaving method/path/rest of
 * the string intact (mirrors nginx AC2 — the log line stays diagnosable).
 *
 * CORE-1405 — the same pass blanks email addresses and drops `user.email`:
 * an address is personal data, Sentry is a third party, and it rides in
 * through error texts («User x@y.ru not found») as easily as through the scope.
 *
 * Pure, dependency-free and platform-agnostic so it can be unit-tested in the
 * `node` jest env and reused by both `beforeSend` and `beforeBreadcrumb`.
 */

/**
 * Query keys whose value is a bearer credential (JWT family). Matched
 * case-insensitively. Kept deliberately narrow — only auth tokens, not every
 * `token=` — so ordinary diagnostic query params survive for triage.
 */
export const SENSITIVE_QUERY_KEYS = [
  'access_token',
  'id_token',
  'refresh_token',
] as const;

const REDACTED = 'REDACTED';

// (start | ?&)<key>=<value> — the key must sit at a param boundary (string
// start, '?' or '&') so `my_access_token` isn't matched, and the value runs
// until the next '&', whitespace or quote so the match never swallows the rest
// of a log line / JSON blob. Start anchoring also covers the bare
// `query_string: "access_token=…"` form Sentry attaches. Key + delimiter are
// preserved verbatim; only the value is replaced.
const SECRET_QUERY_RE = new RegExp(
  `(^|[?&])((?:${SENSITIVE_QUERY_KEYS.join('|')})=)[^&\\s"'\\\\]+`,
  'gi',
);

/**
 * Replace the value of any sensitive query parameter inside an arbitrary string
 * (full URL, request line, or free text that embeds one) with `REDACTED`.
 * Everything else — scheme, host, path, other params, order — is untouched.
 */
export function redactUrlSecrets(input: string): string {
  if (!input || input.indexOf('=') === -1) return input;
  return input.replace(SECRET_QUERY_RE, `$1$2${REDACTED}`);
}

// Parts are length-bounded so a long line without an address stays linear to
// scan. Mirrors the backend SecretScrubber.EmailAddress.
const EMAIL_RE = /[A-Za-z0-9._%+-]{1,64}@(?:[A-Za-z0-9-]{1,63}\.){1,8}[A-Za-z]{2,24}\b/g;

/** CORE-1405 — replace every email address in a string with `REDACTED`. */
export function redactEmails(input: string): string {
  if (!input || input.indexOf('@') === -1) return input;
  return input.replace(EMAIL_RE, REDACTED);
}

/** Everything a free-text field must lose: URL secrets and email addresses. */
function redactText(input: string): string {
  return redactEmails(redactUrlSecrets(input));
}

/** Redact a value only when it is a string; pass anything else through. */
function redactMaybe<T>(value: T): T {
  return typeof value === 'string' ? (redactText(value) as unknown as T) : value;
}

/**
 * Scrub every place a hub URL can surface on a Sentry breadcrumb: the
 * `http`/`fetch`/`xhr` breadcrumb carries the target URL in `data.url`, and the
 * `message` often embeds it too ("Failed to load resource: …?access_token=…").
 * Returns the same object (mutated) for use as a `beforeBreadcrumb` result.
 */
export function scrubSentryBreadcrumb<T extends { message?: unknown; data?: unknown } | null>(
  breadcrumb: T,
): T {
  if (!breadcrumb) return breadcrumb;
  if (typeof breadcrumb.message === 'string') {
    breadcrumb.message = redactText(breadcrumb.message);
  }
  const data = breadcrumb.data;
  if (data && typeof data === 'object') {
    const bag = data as Record<string, unknown>;
    for (const key of Object.keys(bag)) {
      bag[key] = redactMaybe(bag[key]);
    }
  }
  return breadcrumb;
}

/**
 * Scrub every place a hub URL can surface on a Sentry event before it is sent:
 * the request URL + query string, the top-level message, the exception values'
 * messages, the transaction name and any breadcrumbs already attached. Also
 * drops `user.email` (CORE-1405). Returns
 * the same object (mutated) for use as a `beforeSend` result.
 */
export function scrubSentryEvent<
  T extends {
    message?: unknown;
    transaction?: unknown;
    request?: { url?: unknown; query_string?: unknown } | undefined;
    user?: { email?: unknown } | undefined;
    exception?: { values?: Array<{ value?: unknown }> } | undefined;
    breadcrumbs?: Array<{ message?: unknown; data?: unknown }> | undefined;
  } | null,
>(event: T): T {
  if (!event) return event;

  // CORE-1405 — the user is identified by id only.
  if (event.user && 'email' in event.user) {
    delete event.user.email;
  }

  if (typeof event.message === 'string') {
    event.message = redactText(event.message);
  }
  if (typeof event.transaction === 'string') {
    event.transaction = redactText(event.transaction);
  }

  const request = event.request;
  if (request) {
    if (typeof request.url === 'string') request.url = redactText(request.url);
    // query_string can be a string, an object or an array of pairs; only the
    // string form carries the raw `access_token=…` we need to blank.
    if (typeof request.query_string === 'string') {
      request.query_string = redactText(request.query_string);
    }
  }

  const values = event.exception?.values;
  if (Array.isArray(values)) {
    for (const v of values) {
      if (v && typeof v.value === 'string') v.value = redactText(v.value);
    }
  }

  if (Array.isArray(event.breadcrumbs)) {
    for (const b of event.breadcrumbs) scrubSentryBreadcrumb(b);
  }

  return event;
}
